import { Injectable } from '@angular/core';
import { AppPageEnum, EventName } from 'app/shared/enums';
import { Subject, Subscription } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class EventService {

    // skipcq: JS-0323
    private _eventSubjects: Map<string, Subject<any>> = new Map();
    private _subscriptions: Array<{eventName: string, scope: string, subscription: Subscription}> = [];

    private getEventKey(eventName: EventName, scope: string): string {
        return `${eventName}_${scope}`;
    }

    // skipcq: JS-0323
    private getOrCreateSubject(eventName: EventName, scope: string): Subject<any> {
        const key = this.getEventKey(eventName, scope);
        if (!this._eventSubjects.has(key)) {
            this._eventSubjects.set(key, new Subject<any>());
        }
        return this._eventSubjects.get(key)!;
    }

    // skipcq: JS-0323
    on(eventName: EventName, callback: (data: any) => void, scope: string = AppPageEnum.Base): Subscription {
        const subject = this.getOrCreateSubject(eventName, scope);
        const subscription = subject.subscribe(callback);
        
        this._subscriptions.push({ eventName, scope, subscription });
        
        return subscription;
    }

    off(eventName: EventName, scope: string = AppPageEnum.Base) {
        // Unsubscribe all subscriptions for this event and scope
        const subscriptionsToRemove = this._subscriptions.filter(
            sub => sub.eventName === eventName && sub.scope === scope
        );
        
        subscriptionsToRemove.forEach(sub => {
            sub.subscription.unsubscribe();
            const index = this._subscriptions.indexOf(sub);
            if (index !== -1) {
                this._subscriptions.splice(index, 1);
            }
        });

        // Clean up the subject if no more subscriptions
        const key = this.getEventKey(eventName, scope);
        const hasActiveSubscriptions = this._subscriptions.some(
            sub => sub.eventName === eventName && sub.scope === scope
        );
        
        if (!hasActiveSubscriptions && this._eventSubjects.has(key)) {
            const subject = this._eventSubjects.get(key);
            subject?.complete();
            this._eventSubjects.delete(key);
        }
    }

    // skipcq: JS-0323
    broadcast(eventName: EventName, data: any, scope: string = AppPageEnum.Base) {
        const subject = this.getOrCreateSubject(eventName, scope);
        subject.next(data);
    }

    // Optional: Method to unsubscribe a specific subscription
    unsubscribe(subscription: Subscription) {
        const index = this._subscriptions.findIndex(sub => sub.subscription === subscription);
        if (index !== -1) {
            subscription.unsubscribe();
            this._subscriptions.splice(index, 1);
        }
    }

    // Clean up all subscriptions (useful for testing or app shutdown)
    destroy() {
        this._subscriptions.forEach(sub => sub.subscription.unsubscribe());
        this._subscriptions = [];
        
        this._eventSubjects.forEach(subject => subject.complete());
        this._eventSubjects.clear();
    }
}
